import type { PlanDay, PlanInput } from '@painpal/contracts';
import { weekEnd, weekStart } from '@painpal/core';
import { Plus } from 'lucide-react';
import { useRef, useState } from 'react';
import { useSavePlanDay } from '../../api/mutations';
import { usePlan, useRecommendations } from '../../api/queries';
import { Eyebrow } from '../../components/Eyebrow';
import { Headline } from '../../components/Headline';
import { ErrorPanel, LoadingBlock } from '../../components/StateViews';
import { errorMessage } from '../../lib/api';
import { formatWeekdayMonthDay } from '../../lib/format';
import { Presence } from '../../ui/Presence';
import { addLabel, recommendationOn, suggestionInput, type ActiveRecommendation } from '../recommendations/recommendationModel';
import { SwapStrip } from '../recommendations/SwapStrip';
import { useDismissals } from '../recommendations/useDismissals';
import { CalendarRow } from './CalendarRow';
import { CalendarWeekHeader } from './CalendarWeekHeader';
import { CopyLastWeek } from './CopyLastWeek';
import { DayEditor } from './DayEditor';
import { isoWeek, progressPercent, ridingDaysLine, weekPlanTotals, type WeekPlanTotals } from './planModel';
import styles from './calendar.module.css';

export interface CalendarPageProps { today: string; week: string }

function PageHeader({ today, week, totals }: { today: string; week: string; totals: WeekPlanTotals | undefined }) {
  return (
    <header className={styles.header}>
      <div className="page-heading">
        <Eyebrow>
          {formatWeekdayMonthDay(today)} · WEEK {isoWeek(week)}
          {totals !== undefined && <span className="sr-only-phone"> · {ridingDaysLine(totals)}</span>}
        </Eyebrow>
        <Headline first="PLAN THE WEEK," second="RIDE THE DAY." />
      </div>
      <p className={styles.hint}>CLICK A DAY TO EDIT ITS BLOCK<span className={styles.hintQuiet}>LOAD IS AN ESTIMATE UNTIL YOU RIDE</span></p>
    </header>
  );
}

function Progress({ totals }: { totals: WeekPlanTotals }) {
  return (
    <div className={styles.progress} aria-hidden="true">
      <span className={styles.progressFill} data-part="progress" style={{ width: `${progressPercent(totals)}%` }} />
    </div>
  );
}

function ColumnHeads() {
  return <div className={styles.heads} aria-hidden="true"><span>DAY</span><span>PLAN</span><span>NOTE</span><span>EST. LOAD</span><span>RIDDEN</span><span /></div>;
}

interface WeekRowProps {
  day: PlanDay; today: string; expanded: boolean; onOpen: () => void; onClose: () => void;
  active: ActiveRecommendation | null; onDismiss: (basis: string) => void;
}

// Each row item anchors its own editor, so the popover sits by the row and an outside click is outside the item.
// An empty day can carry a suggestion (+ adds it); a planned day can carry a swap strip. Neither counts in the totals.
// Adding or dismissing removes the focused button, so focus goes back to the row's own Edit button.
// Once SWAP lands, the plan can refetch before the recommendations do; the swap is done, so it isn't offered again.
function WeekRow({ day, today, expanded, onOpen, onClose, active, onDismiss }: WeekRowProps) {
  const row = useRef<HTMLButtonElement>(null);
  const save = useSavePlanDay();
  const keep = (input: PlanInput): void => save.mutate({ date: day.date, input }, { onSuccess: () => row.current?.focus() });
  const suggestion = day.plan === null ? active?.suggestion ?? null : null;
  const offered = active?.conflict ?? null;
  const conflict = day.plan === null || offered?.alternative.type === day.plan.type ? null : offered;
  const basis = active?.basis ?? '';
  const dismiss = (): void => {
    onDismiss(basis);
    row.current?.focus();
  };
  // The editor saves through its own mutation, so an old + failure shouldn't linger once it opens.
  const open = (): void => {
    save.reset();
    onOpen();
  };
  return (
    <li className={styles.rowItem} data-editor-anchor="">
      <CalendarRow ref={row} day={day} today={today} expanded={expanded} onEdit={open} suggestion={suggestion} />
      {suggestion !== null && (
        <button type="button" className={styles.add} aria-label={addLabel(suggestion, day.date)} disabled={save.isPending} onClick={() => keep(suggestionInput(suggestion))}>
          <Plus size={16} aria-hidden="true" />
        </button>
      )}
      {conflict !== null && (
        <div className={styles.swapRow}>
          <SwapStrip conflict={conflict} saving={save.isPending} onKeep={dismiss} onSwap={() => keep(suggestionInput(conflict.alternative))} />
        </div>
      )}
      {save.error !== null && <p role="alert" className={`alert-hot ${styles.rowAlert}`}>{errorMessage(save.error)}</p>}
      <Presence show={expanded}><DayEditor day={day} suggestion={suggestion} onClose={onClose} /></Presence>
    </li>
  );
}

interface RowsProps {
  days: readonly PlanDay[]; today: string; open: string | null; onOpen: (date: string) => void; onClose: () => void;
  active: (date: string) => ActiveRecommendation | null; onDismiss: (date: string, basis: string) => void;
}

function WeekRows({ days, today, open, onOpen, onClose, active, onDismiss }: RowsProps) {
  return (
    <ol className={styles.rows} aria-label="Days">
      {days.map((day) => (
        <WeekRow
          key={day.date} day={day} today={today} expanded={open === day.date} onOpen={() => onOpen(day.date)} onClose={onClose}
          active={active(day.date)} onDismiss={(basis) => onDismiss(day.date, basis)}
        />
      ))}
    </ol>
  );
}

export function CalendarPage({ today: browserToday, week }: CalendarPageProps) {
  const plan = usePlan(week, weekEnd(week));
  // Recommendations only cover today to Sunday, so other weeks find none.
  const recommendations = useRecommendations();
  const dismissals = useDismissals(browserToday);
  // The statuses were judged against the profile zone's today, so TODAY marks that day once the server has said.
  const today = plan.data?.today ?? browserToday;
  const [editing, setEditing] = useState<string | null>(null);
  const [editingWeek, setEditingWeek] = useState(week);
  // Paging closes the editor, so coming back to its week (back, forward, keyboard) doesn't reopen it.
  if (editingWeek !== week) {
    setEditingWeek(week);
    setEditing(null);
  }
  // While the next week loads the previous week's rows stay (faded), but an editor only opens on the shown week.
  const open = editing !== null && editing >= week && editing <= weekEnd(week) ? editing : null;
  const days = plan.data?.days;
  const totals = days === undefined ? undefined : weekPlanTotals(days);
  return (
    <div className="page">
      <PageHeader today={today} week={week} totals={totals} />
      <section className={styles.card} aria-label="Week" aria-busy={plan.isPlaceholderData} data-placeholder={plan.isPlaceholderData || undefined}>
        <CalendarWeekHeader week={week} thisWeek={weekStart(browserToday)} totals={totals} />
        {plan.isPending && <LoadingBlock label="LOADING THE WEEK" height={448} />}
        {plan.isError && days === undefined && <ErrorPanel error={plan.error} onRetry={() => void plan.refetch()} />}
        {days !== undefined && totals !== undefined && (
          <>
            {totals.blocks > 0 ? <><Progress totals={totals} /><ColumnHeads /></> : <CopyLastWeek key={week} week={week} disabled={plan.isPlaceholderData} />}
            <WeekRows
              days={days} today={today} open={open} onOpen={setEditing} onClose={() => setEditing(null)}
              active={(date) => recommendationOn(recommendations.data, date, dismissals.isDismissed)} onDismiss={dismissals.dismiss}
            />
          </>
        )}
      </section>
      {days?.some((day) => day.plan?.defaulted === true) === true && <p className={styles.footnote}>* NO DURATION SET · USES THE TYPE&apos;S DEFAULT</p>}
    </div>
  );
}
